import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { lossFactorsClient } from '@/api'
import { config } from '@/config'
import { UpdateLossFactorsDatasetRequest } from '@/interfaces'

const BASE_ROUTE = config.links.library.lossFactors
const BASE_QUERY_KEY = lossFactorsClient.QUERY_KEY

export const useLossFactorsDetails = (datasetId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, datasetId]

  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()

  const getLossFactorsQuery = useQuery({
    queryKey: [...BASE_QUERY_KEY, { lockedOnly: false }],
    queryFn: () => lossFactorsClient.getAll(),
    select: datasets => datasets.filter(x => !!x.name).map(x => x.name || '')
  })

  const datasetInfoQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => lossFactorsClient.getById(datasetId)
  })

  const updateDatasetMutation = useMutation({
    mutationFn: (req: UpdateLossFactorsDatasetRequest) =>
      lossFactorsClient.updateDataset(datasetId, req),
    onSuccess: async () => {
      showSuccess('Dataset successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const cloneDatasetMutation = useMutation({
    mutationFn: (datasetName: string) =>
      lossFactorsClient.cloneDataset({ datasetId, name: datasetName }),
    onSuccess: clonedDatasetId => {
      showSuccess('Dataset successfully cloned')
      navigate(`${BASE_ROUTE}/${clonedDatasetId}`)
    }
  })

  const removeDatasetMutation = useMutation({
    mutationFn: () => lossFactorsClient.removeDataset(datasetId),
    onSuccess: () => {
      showSuccess('Dataset successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const lockDatasetMutation = useMutation({
    mutationFn: () => lossFactorsClient.lockDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully locked')
      await queryClient.invalidateQueries({ queryKey: BASE_QUERY_KEY })
    }
  })

  const archiveDatasetMutation = useMutation({
    mutationFn: () => lossFactorsClient.archiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully archived')
      await queryClient.invalidateQueries({ queryKey: BASE_QUERY_KEY })
    }
  })

  const unArchiveDatasetMutation = useMutation({
    mutationFn: () => lossFactorsClient.unArchiveDataset(datasetId),
    onSuccess: async () => {
      showSuccess('Dataset successfully unarchived')
      await queryClient.invalidateQueries({ queryKey: BASE_QUERY_KEY })
    }
  })

  return {
    datasetInfo: datasetInfoQuery.data,
    allDatasetNames: getLossFactorsQuery.data || [],
    isFetching: datasetInfoQuery.isFetching,
    updateDataset: updateDatasetMutation.mutate,
    isUpdateDatasetInProgress: updateDatasetMutation.isPending,
    cloneDataset: cloneDatasetMutation.mutate,
    isCloneDatasetInProgress: cloneDatasetMutation.isPending,
    removeDataset: removeDatasetMutation.mutate,
    lockDataset: lockDatasetMutation.mutate,
    archiveDataset: archiveDatasetMutation.mutate,
    unArchiveDataset: unArchiveDatasetMutation.mutate
  }
}
